import { useEffect, useRef, useState } from "react";
import { ExternalLink, Upload } from "lucide-react";
import { Button, FormDialog, Icon, PlatformIcon, Spinner, StatBlock, compactNumber, formatRelativeTime } from "@cntrl/ui";
import { errorMessage, formErrorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { useUploadMedia } from "../../app/hooks/useMedia.js";
import {
  isJobSettled,
  usePostAnalysisJob,
  usePostStats,
  useSaveIdea,
  useStartPostAnalysis,
  type ContentIdea,
  type PostAnalysis,
  type PostStats,
} from "../../app/hooks/useContentStats.js";
import { RetentionChart } from "./RetentionChart.js";
import { pickCookingLine } from "./cookingLines.js";
import styles from "./GetTheData.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/** Mirrors the server's PostAnalysisLimits.MaxVideoBytes — checked here only to fail before a long upload. */
const MAX_UPLOAD_BYTES = 300 * 1024 * 1024;
/** After this, a job that has not settled is reported as stuck rather than polled forever. */
const JOB_PATIENCE_MS = 6 * 60 * 1000;

const PLATFORM_NAME: Record<string, string> = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };

function seconds(s: number): string {
  if (s < 60) return `${Math.round(s * 10) / 10}s`;
  const r = Math.round(s);
  return `${Math.floor(r / 60)}:${String(r % 60).padStart(2, "0")}`;
}

/**
 * "Get the data" (ART-262): paste a link to one of the band's own posts. Like "Need ideas", the work happens in a
 * dialog opened at the press: the stats load first (a few seconds) and show as soon as they land, then the long
 * part — Gemini watching the post, 30–90 s — runs as a job behind the same rotating loading lines, polled until it
 * settles.
 *
 * What the model saw is ALWAYS said, never implied. TikTok serves no video file to any app, so a TikTok analysis
 * reads the cover frame, caption and numbers — and says so — unless the band uploads the video themselves.
 */
export function GetTheData() {
  const [url, setUrl] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number>(0);
  const [now, setNow] = useState(() => Date.now());

  const stats = usePostStats();
  const start = useStartPostAnalysis();
  const job = usePostAnalysisJob(jobId);

  const settled = isJobSettled(job.data);
  const running = jobId != null && !settled && !job.error;
  const stuck = running && now - startedAt > JOB_PATIENCE_MS;

  // A clock only while a job runs, so "taking longer than usual" can appear without a refetch.
  useEffect(() => {
    if (!running) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, [running]);

  const startAnalysis = (postUrl: string, mediaId?: string) => {
    setJobId(null);
    start.mutate(
      { url: postUrl, mediaId: mediaId ?? null },
      {
        onSuccess: (j) => {
          setStartedAt(Date.now());
          setNow(Date.now());
          setJobId(j.jobId ?? null);
        },
      },
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    setSubmittedUrl(trimmed);
    setJobId(null);
    start.reset();
    setOpen(true);
    stats.mutate({ url: trimmed }, { onSuccess: () => startAnalysis(trimmed) });
  };

  // Closing abandons the view, not the job: a running analysis finishes in the worker and simply goes unread.
  const close = () => {
    setOpen(false);
    setJobId(null);
    stats.reset();
    start.reset();
  };

  // Back to the card with an empty field and the cursor in it. After the dialog unmounts, because closing hands focus
  // back to whatever opened it (the Analyze button) and that would land second.
  const tryAnother = () => {
    close();
    setUrl("");
    requestAnimationFrame(() => input.current?.focus());
  };

  const post = job.data?.stats ?? stats.data ?? null;
  const analyzing = start.isPending || (running && !stuck);
  const name = post ? PLATFORM_NAME[post.platform ?? ""] : null;
  // ART-313: on desktop the dialog is wide from the moment it opens (no width jump when the numbers land) and splits
  // into two columns — the performance on the left, the analysis on the right — each scrolling on its own, because
  // either can be the long one. A phone keeps the stacked sheet.
  const isDesktop = !usePhoneLayout();

  return (
    <section className={styles.box} aria-labelledby="content-analyze-heading">
      <header>
        <h3 id="content-analyze-heading" className={styles.title}>Get the data</h3>
        <p className={styles.copy}>Paste a link to one of your Instagram, Facebook or TikTok posts.</p>
      </header>

      <form className={styles.controls} onSubmit={submit}>
        <input
          ref={input}
          className={styles.input}
          placeholder="instagram.com/reel/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="Post link"
          inputMode="url"
          autoComplete="off"
        />
        <Button type="submit" variant="accent" size="field" disabled={!url.trim()}>Analyze</Button>
      </form>

      {open && (
        <FormDialog
          title={name ? `${name} post` : "Get the data"}
          size={isDesktop ? "3xl" : "lg"}
          bodyLayout={isDesktop ? "flush" : "inset"}
          onClose={close}
          dirty={false}
          footer={
            // Once nothing is loading — after a result, a failure, or a link that was not the band's post.
            !stats.isPending && !analyzing ? (
              <Button variant="ghost" onClick={tryAnother}>Try another link</Button>
            ) : null
          }
        >
          <div className={styles.dialogBody} data-split={isDesktop ? "" : undefined}>
            <div className={styles.column} data-column="performance">
              {stats.isPending && (
                <p className={styles.progress}><Spinner size={14} /> Getting the numbers…</p>
              )}
              {stats.isError && (
                <p className={styles.error} role="alert">{formErrorMessage(stats.error, "Couldn't read that post. Try again.")}</p>
              )}

              {post && <PostStatsPanel post={post} />}
            </div>

            {post && submittedUrl && (
              <div className={styles.column} data-column="analysis">
                <AnalysisPanel
                  platform={post.platform ?? ""}
                  analyzing={analyzing}
                  stuck={stuck}
                  startError={start.isError ? formErrorMessage(start.error, "Couldn't start the analysis. Try again.") : null}
                  pollError={job.isError ? formErrorMessage(job.error, "Lost track of the analysis. Run it again.") : null}
                  failed={job.data?.status === "failed" ? job.data.message ?? "The analysis failed. Try again." : null}
                  analysis={job.data?.status === "done" ? job.data.analysis ?? null : null}
                  onRetry={() => startAnalysis(submittedUrl)}
                  onUploaded={(mediaId) => startAnalysis(submittedUrl, mediaId)}
                />
              </div>
            )}
          </div>
        </FormDialog>
      )}
    </section>
  );
}

/** The loading line for the long wait. "Need ideas" waits seconds and keeps one line; this waits a minute or more,
 *  so the line moves on every few seconds — a single sentence held for 90 s reads as a hang. */
function useRotatingLine(active: boolean, everyMs = 6000): string {
  const [line, setLine] = useState(() => pickCookingLine());
  useEffect(() => {
    if (!active) return undefined;
    setLine((l) => pickCookingLine(l));
    const t = window.setInterval(() => setLine((l) => pickCookingLine(l)), everyMs);
    return () => window.clearInterval(t);
  }, [active, everyMs]);
  return line;
}

function PostStatsPanel({ post }: { post: PostStats }) {
  const name = PLATFORM_NAME[post.platform ?? ""] ?? "Post";
  const posted = post.postedOn
    ? new Date(post.postedOn).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  // One Reel on two surfaces: the tiles carry Meta's COMBINED counts, and everything that exists per surface (reach,
  // watch time, skip rate…) moves into the Instagram | Facebook table below them.
  const split = post.crossPost && post.crossPost.length > 0 ? post.crossPost : null;
  const completion =
    post.avgWatchSeconds != null && post.durationSeconds ? Math.round((post.avgWatchSeconds / post.durationSeconds) * 100) : null;

  // Only what the platform publishes: an absent figure is not a tile at all (never a 0, and not a column of dashes
  // for metrics a platform simply does not have).
  const tiles: Array<{ label: string; value: number; suffix?: string; exact?: boolean }> = [];
  const add = (label: string, value: number | null | undefined, suffix?: string, exact?: boolean) => {
    if (value != null) tiles.push({ label, value, suffix, exact });
  };
  add("Views", post.views);
  if (!split) add("Reached", post.reach);
  add("Likes", post.likes);
  add("Comments", post.comments);
  if (!split) {
    add("Shares", post.shares);
    add("Saves", post.saves);
    add("Watched to end", post.completeViews);
    add("Skipped in 3s", post.skipRatePercent != null ? Math.round(post.skipRatePercent * 10) / 10 : null, "%", true);
  }

  return (
    <div className={styles.post}>
      <div className={styles.postHead}>
        {post.thumbnailUrl && (
          <img className={styles.thumb} src={post.thumbnailUrl} alt="" loading="lazy"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div className={styles.postMeta}>
          <span className={styles.platform}>
            <PlatformIcon platform={post.platform ?? ""} size={16} /> {name}
            {posted && <> · {posted}</>}
            {post.permalink && (
              <a className={styles.open} href={post.permalink} target="_blank" rel="noreferrer">
                Open post <Icon icon={ExternalLink} size={16} />
              </a>
            )}
          </span>
          {post.caption && <p className={styles.caption}>{post.caption}</p>}
          {!post.live && post.fetchedOn && (
            <span className={styles.asOf}>From the last sync, {formatRelativeTime(post.fetchedOn)}</span>
          )}
        </div>
      </div>

      {post.benchmark && <Verdict benchmark={post.benchmark} />}

      {tiles.length > 0 && (
        <div className={styles.tiles}>
          {tiles.map((t) => <StatBlock key={t.label} label={t.label} value={t.value} suffix={t.suffix} exact={t.exact} />)}
        </div>
      )}

      {split && <CrossPostTable sides={split} durationSeconds={post.durationSeconds} />}

      {!split && post.avgWatchSeconds != null && (
        <p className={styles.watch}>
          People watched <strong>{seconds(post.avgWatchSeconds)}</strong> on average
          {post.durationSeconds ? <> of {seconds(post.durationSeconds)}{completion != null && <> ({completion}%)</>}</> : null}.
        </p>
      )}

      {post.retention && post.retention.length >= 2 && (
        <div className={styles.retention}>
          <h4 className={styles.subTitle}>
            Where viewers dropped off
            {post.retentionPlatform && post.retentionPlatform !== post.platform && <> · {PLATFORM_NAME[post.retentionPlatform]}</>}
          </h4>
          <RetentionChart
            points={post.retention.map((p) => ({ position: p.position ?? 0, retained: p.retained ?? 0 }))}
            durationSeconds={post.durationSeconds}
          />
        </div>
      )}
    </div>
  );
}

const FORMAT_NAME: Record<string, [string, string]> = {
  reel: ["Reel", "Reels"], video: ["video", "videos"], carousel: ["carousel", "carousels"], photo: ["photo post", "photo posts"],
};

/** The measured verdict: this post against the band's own usual for the same platform and format. Never the model's
 *  call. Too young a post says so; too few comparable posts shows nothing. */
function Verdict({ benchmark }: { benchmark: NonNullable<PostStats["benchmark"]> }) {
  const platform = PLATFORM_NAME[benchmark.platform ?? ""] ?? "";
  const [one, many] = FORMAT_NAME[benchmark.format ?? ""] ?? ["post", "posts"];

  if (benchmark.verdict && benchmark.ratio != null && benchmark.medianViews != null) {
    return (
      <div className={styles.verdict}>
        <span className={styles.verdictBadge} data-verdict={benchmark.verdict}>{benchmark.verdict}</span>
        <span className={styles.verdictText}>
          <strong>{benchmark.ratio}×</strong> your usual {platform} {one}
          <span className={styles.verdictBasis}>
            {" "}· median {compactNumber(benchmark.medianViews)} views across your last {benchmark.comparedPosts} {many}
          </span>
        </span>
      </div>
    );
  }
  if (benchmark.reason === "too-early")
    return <p className={styles.verdictNote}>Too early to call. Posts under a week old are still collecting views.</p>;
  // Not enough comparable posts: nothing at all, rather than a line about the gap.
  return null;
}

type Side = NonNullable<PostStats["crossPost"]>[number];

/** Instagram | Facebook, row by row. A row both surfaces leave blank is dropped; a figure only one publishes shows
 *  a dash on the other side. Watch time reads as a share of the video when its length is known. */
function CrossPostTable({ sides, durationSeconds }: { sides: readonly Side[]; durationSeconds?: number | null }) {
  const count = (v: number | null | undefined) => (v != null ? compactNumber(v) : null);
  const watch = (v: number | null | undefined) =>
    v != null ? `${seconds(v)}${durationSeconds ? ` (${Math.round((v / durationSeconds) * 100)}%)` : ""}` : null;
  const rows: Array<[string, (s: Side) => string | null]> = [
    ["Views", (s) => count(s.views)],
    ["Reached", (s) => count(s.reach)],
    ["Likes", (s) => count(s.likes)],
    ["Comments", (s) => count(s.comments)],
    ["Shares", (s) => count(s.shares)],
    ["Saves", (s) => count(s.saves)],
    ["Avg watch", (s) => watch(s.avgWatchSeconds)],
    ["Skipped in 3s", (s) => (s.skipRatePercent != null ? `${Math.round(s.skipRatePercent * 10) / 10}%` : null)],
  ];
  const shown = rows.filter(([, read]) => sides.some((s) => read(s) != null));

  return (
    <table className={styles.split}>
      <caption className={styles.srOnly}>Instagram and Facebook, side by side</caption>
      <thead>
        <tr>
          <td />
          {sides.map((s) => (
            <th key={s.platform} scope="col">
              <span className={styles.splitHead}>
                <PlatformIcon platform={s.platform ?? ""} size={16} /> {PLATFORM_NAME[s.platform ?? ""] ?? s.platform}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {shown.map(([label, read]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            {sides.map((s) => <td key={s.platform}>{read(s) ?? "—"}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface AnalysisPanelProps {
  platform: string;
  analyzing: boolean;
  stuck: boolean;
  startError: string | null;
  pollError: string | null;
  failed: string | null;
  analysis: PostAnalysis | null;
  onRetry: () => void;
  onUploaded: (mediaId: string) => void;
}

function AnalysisPanel(props: AnalysisPanelProps) {
  const { platform, analyzing, stuck, startError, pollError, failed, analysis, onRetry, onUploaded } = props;
  const error = startError ?? pollError ?? failed;
  const line = useRotatingLine(analyzing);

  return (
    <div className={styles.analysis} aria-live="polite">
      <h4 className={styles.subTitle}>Analysis</h4>

      {analyzing && (
        <div className={styles.cooking}>
          <p className={styles.cookingLine}>{line}</p>
          <Spinner size={24} label={line} />
        </div>
      )}
      {stuck && (
        <p className={styles.error}>
          This is taking much longer than usual. <button type="button" className={styles.link} onClick={onRetry}>Run it again</button>
        </p>
      )}
      {error && !analyzing && (
        <p className={styles.error} role="alert">
          {error} <button type="button" className={styles.link} onClick={onRetry}>Try again</button>
        </p>
      )}

      {analysis && !analyzing && <AnalysisResult analysis={analysis} platform={platform} onUploaded={onUploaded} />}
    </div>
  );
}

const BASIS_LABEL: Record<string, string> = {
  video: "Watched the full video",
  image: "Looked at the post's image",
};

function AnalysisResult({ analysis, platform, onUploaded }: { analysis: PostAnalysis; platform: string; onUploaded: (mediaId: string) => void }) {
  const cover = analysis.basis === "cover";
  return (
    <div className={styles.result}>
      {cover ? (
        <div className={styles.coverNote} role="note">
          <p>
            <strong>Not watched.</strong> {PLATFORM_NAME[platform] ?? "This platform"} doesn&apos;t let apps access the
            video file, so this is based on the cover frame, caption and stats. Upload the video to have it watched.
          </p>
          <UploadVideo onUploaded={onUploaded} />
        </div>
      ) : (
        <span className={styles.basis}>{BASIS_LABEL[analysis.basis ?? ""] ?? "Analyzed"}</span>
      )}

      {analysis.summary && <p className={styles.summary}>{analysis.summary}</p>}
      {analysis.hook && <Detail title="The first 3 seconds" body={analysis.hook} />}
      {analysis.dropOff && <Detail title="Where people left" body={analysis.dropOff} />}
      {(analysis.weakSpots ?? []).length > 0 && (
        <div>
          <h5 className={styles.colTitle}>Likely weak spots</h5>
          {/* Said outright: these come from watching, not from viewer data. A measured drop-off (above) only ever
              appears when the platform published one. */}
          <p className={styles.hint}>Our read from watching the video, not measured viewer data.</p>
          <ul className={styles.list}>{(analysis.weakSpots ?? []).map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      <List title="What works" items={analysis.strengths ?? []} />
      <List title="What to avoid" items={analysis.weaknesses ?? []} />
      {(analysis.recommendations ?? []).length > 0 && (
        <TryNext ideas={analysis.recommendations ?? []} platform={platform} />
      )}
    </div>
  );
}

/** The recommendations, each saveable to "Content I'm trying" exactly as a "Need ideas" idea is. */
function TryNext({ ideas, platform }: { ideas: readonly ContentIdea[]; platform: string }) {
  const isDesktop = !usePhoneLayout();
  const save = useSaveIdea();
  const [saved, setSaved] = useState<ReadonlySet<number>>(new Set());

  const saveIdea = (idea: ContentIdea, i: number) =>
    save.mutate(
      { platform, title: idea.title ?? null, body: idea.description ?? null },
      {
        onSuccess: () => {
          setSaved((s) => new Set(s).add(i));
          pushToast("Saved to Content I'm trying", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save idea."), "error"),
      },
    );

  return (
    <div>
      <h5 className={styles.colTitle}>Try next</h5>
      <ol className={styles.ideas}>
        {ideas.map((idea, i) => (
          <li key={i} className={styles.idea}>
            <span className={styles.ideaTitle}>{idea.title}</span>
            {idea.description && <span className={styles.ideaBody}>{idea.description}</span>}
            {saved.has(i) ? (
              <span className={styles.tryThisDone}>Saved</span>
            ) : (
              <Button variant="accent" size={isDesktop ? "xs" : "sm"} className={styles.tryThis}
                      onClick={() => saveIdea(idea, i)} disabled={save.isPending}>
                I&apos;m trying this
              </Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Detail({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h5 className={styles.colTitle}>{title}</h5>
      <p className={styles.detail}>{body}</p>
    </div>
  );
}

function List({ title, items }: { title: string; items: readonly string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h5 className={styles.colTitle}>{title}</h5>
      <ul className={styles.list}>{items.map((s, i) => <li key={i}>{s}</li>)}</ul>
    </div>
  );
}

/** Upload the band's own MP4 so a TikTok can actually be watched. The file goes through the media pipeline and the
 *  job reads it from there — the same bytes the band already has on their phone. */
function UploadVideo({ onUploaded }: { onUploaded: (mediaId: string) => void }) {
  const { uploadMedia } = useUploadMedia();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("That video is over 300 MB. Export a smaller copy and try again.");
      return;
    }
    setProgress(0);
    try {
      const asset = await uploadMedia(file, { kind: "video", purpose: "post-analysis", onProgress: setProgress });
      setProgress(null);
      if (asset.mediaId) onUploaded(asset.mediaId);
    } catch (e) {
      setProgress(null);
      setError(formErrorMessage(e, "The upload failed. Try again."));
    } finally {
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className={styles.upload}>
      <input ref={input} type="file" accept="video/mp4,video/quicktime,video/*" hidden
             onChange={(e) => void pick(e.target.files?.[0])} aria-label="Video file" />
      <Button variant="ghost" size="sm" disabled={progress != null} onClick={() => input.current?.click()}>
        <Icon icon={Upload} size={16} /> {progress != null ? `Uploading… ${Math.round(progress * 100)}%` : "Upload the video"}
      </Button>
      {error && <span className={styles.error} role="alert">{error}</span>}
    </div>
  );
}
